/**
 * Copies a string to the clipboard if the browser supports the clipboard API
 * @param text string content to be copied to the clipboard
 * @returns boolean indicating if the text was copied successfully
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        await window.navigator.clipboard.writeText(text)
        return true
    } catch (e) {
        // browser may not support clipboard API or access is denied
        return false
    }
}

/**
 * Copies the HTML and Plain Text content of an HTMLElement to the clipboard
 * @param innerHTML HTML Text format to be copied
 * @param innerText Plain Text format to be copied
 * @returns boolean indicating if the text was copied successfully
 */
const copyHTMLElement = async (
    innerHTML: string,
    innerText: string
): Promise<boolean> => {
    try {
        const blobHtml = new Blob([innerHTML], { type: 'text/html' })
        const blobPlainText = new Blob([innerText], {
            type: 'text/plain',
        })

        // Add both HTML and Plain Text to the Clipboard
        // When pasted, the app will choose the best format to paste. Example:
        // - When pasting to MS Word, it will paste the HTML content
        // - When pasting to a notepad, it will paste the plain text content
        const clipboardItem = new ClipboardItem({
            'text/html': blobHtml, // For apps that support HTML formatted content
            'text/plain': blobPlainText, // Some apps may not support HTML
        })

        await window.navigator.clipboard.write([clipboardItem])
        return true
    } catch (e) {
        // browser may not support clipboard API or access is denied
        return false
    }
}

export { copyHTMLElement, copyToClipboard }
